import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  WizardStage,
  Spread,
  SpreadKind,
  SpreadElement,
  Moment,
  BankImage,
  StoryElement,
  StoryElementKind,
  GenerateFromMomentResponse,
  ImageSizePreset,
  LayoutSpreadResponse,
  RefineTextResponse,
  SplitStoryResponse,
  StoryBeat,
  UpdateBeatBreaksResponse,
} from '@historyjki/shared';
import {
  IMAGE_SIZE_PRESETS,
  SPREAD_TEXT_PANEL_PAD,
  clampTextBoxToSafeArea,
  formatPageInches,
  normalizeSizePreset,
  parseBeatBreaks,
  textOutsetPct,
  textSizeForFontScale,
} from '@historyjki/shared';
import { api, toastError } from '../utils/api';
import { r2Url } from '../utils/r2Url';
import { useToast } from './useToast';
import {
  getCanvasAspect,
  parseSpreadElements,
  spreadLabel,
} from '../utils/spreadGeometry';
import type { useProject } from './useProject';

type ProjectHookReturn = ReturnType<typeof useProject>;

/** A spread with parsed elements — the editor's working unit. */
export interface SpreadView {
  id: string;
  kind: SpreadKind;
  elements: SpreadElement[];
}

/** A draggable tile in the image bank (generated take or cast art). */
export interface BankTile {
  key: string;
  kind: 'take' | 'cast';
  r2_key: string | null;
  caption: string;
  bankImage?: BankImage;
  element?: StoryElement;
  castKind?: StoryElementKind;
  stale?: boolean;
  stale_reason?: string | null;
}

/** A generation request in flight — renders shimmer tiles in the bank. */
export interface PendingGeneration {
  key: string;
  count: number;
}

export type SaveState = 'saved' | 'saving' | 'error';

const SAVE_DEBOUNCE_MS = 700;

let localElCounter = 0;
const newElementId = () =>
  `el-${Date.now().toString(36)}-${(++localElCounter).toString(36)}-${Math.random().toString(36).slice(2, 6)}`;

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export function useComposeStage(projectHook: ProjectHookReturn) {
  const { project, refetch, updateProject } = projectHook;
  const { showToast } = useToast();
  const projectId = project?.id;

  // ── Local working copy of the spreads (instant WYSIWYG edits) ────────
  const [spreads, setSpreads] = useState<SpreadView[]>([]);
  const dirtyRef = useRef<Map<string, SpreadElement[]>>(new Map());
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [saveState, setSaveState] = useState<SaveState>('saved');

  // Sync from the server copy, keeping locally-dirty spreads as-is so an
  // in-flight refetch never clobbers unsaved canvas edits.
  useEffect(() => {
    const server = (project?.spreads ?? []).map<SpreadView>((s: Spread) => ({
      id: s.id,
      kind: s.kind,
      elements: parseSpreadElements(s.elements),
    }));
    setSpreads((prev) => {
      const prevById = new Map(prev.map((s) => [s.id, s]));
      return server.map((s) =>
        dirtyRef.current.has(s.id) ? (prevById.get(s.id) ?? s) : s,
      );
    });
  }, [project?.spreads]);

  // ── Selection / navigation ────────────────────────────────────────────
  const [currentId, setCurrentId] = useState<string | null>(null);
  const [selectedEl, setSelectedEl] = useState<string | null>(null);

  useEffect(() => {
    if (spreads.length === 0) {
      if (currentId !== null) setCurrentId(null);
      return;
    }
    if (!currentId || !spreads.some((s) => s.id === currentId)) {
      const firstInterior = spreads.find((s) => s.kind === 'spread');
      setCurrentId((firstInterior ?? spreads[0]).id);
      setSelectedEl(null);
    }
  }, [spreads, currentId]);

  const idx = spreads.findIndex((s) => s.id === currentId);
  const current: SpreadView | null = idx >= 0 ? spreads[idx] : null;

  const interiorIndexOf = useCallback(
    (spreadId: string) =>
      spreads.filter((s) => s.kind === 'spread').findIndex((s) => s.id === spreadId),
    [spreads],
  );

  const labelFor = useCallback(
    (spread: SpreadView) => spreadLabel(spread, interiorIndexOf(spread.id)),
    [interiorIndexOf],
  );

  // ── Persistence (debounced per-spread PATCH) ─────────────────────────
  const flushDirty = useCallback(async () => {
    if (!projectId) return;
    const entries = [...dirtyRef.current.entries()];
    if (entries.length === 0) return;
    setSaveState('saving');
    let failed = false;
    for (const [spreadId, elements] of entries) {
      try {
        await api.patch(`/projects/${projectId}/spreads/${spreadId}`, { elements });
        // Only clear if no newer edit landed while the PATCH was in flight.
        if (dirtyRef.current.get(spreadId) === elements) {
          dirtyRef.current.delete(spreadId);
        }
      } catch (err) {
        failed = true;
        toastError(showToast, err, 'Failed to save spread');
      }
    }
    setSaveState(failed ? 'error' : 'saved');
  }, [projectId, showToast]);

  const scheduleSave = useCallback(() => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      saveTimerRef.current = null;
      void flushDirty();
    }, SAVE_DEBOUNCE_MS);
  }, [flushDirty]);

  // Flush pending edits when the editor unmounts (stage switch, nav away).
  useEffect(() => {
    return () => {
      if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
      void flushDirty();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const mutateElements = useCallback(
    (spreadId: string, fn: (els: SpreadElement[]) => SpreadElement[]) => {
      setSpreads((prev) =>
        prev.map((s) => {
          if (s.id !== spreadId) return s;
          const next = fn(s.elements);
          dirtyRef.current.set(spreadId, next);
          return { ...s, elements: next };
        }),
      );
      setSaveState('saving');
      scheduleSave();
    },
    [scheduleSave],
  );

  // ── Element operations (on the current spread) ───────────────────────
  const updateEl = useCallback(
    (elId: string, patch: Partial<SpreadElement>) => {
      if (!currentId) return;
      mutateElements(currentId, (els) =>
        els.map((e) => (e.id === elId ? ({ ...e, ...patch } as SpreadElement) : e)),
      );
    },
    [currentId, mutateElements],
  );

  const addEl = useCallback(
    (el: Omit<SpreadElement, 'id' | 'z'>) => {
      if (!currentId) return null;
      const id = newElementId();
      mutateElements(currentId, (els) => {
        const z = els.reduce((m, e) => Math.max(m, e.z), 0) + 1;
        return [...els, { ...(el as SpreadElement), id, z }];
      });
      setSelectedEl(id);
      return id;
    },
    [currentId, mutateElements],
  );

  const removeEl = useCallback(
    (elId: string) => {
      if (!currentId) return;
      mutateElements(currentId, (els) => els.filter((e) => e.id !== elId));
      setSelectedEl((sel) => (sel === elId ? null : sel));
    },
    [currentId, mutateElements],
  );

  const duplicateEl = useCallback(
    (elId: string) => {
      const src = current?.elements.find((e) => e.id === elId);
      if (!src) return;
      const { id: _id, z: _z, ...rest } = src;
      const nudged = {
        x: clamp(src.x + 4, 0, 90),
        y: clamp(src.y + 4, 0, 90),
      };
      // Duplicated text lands inside the print-safe area like any other
      // text placement (images may sit at the page edge).
      const box =
        src.type === 'text' && current
          ? clampTextBoxToSafeArea(
              { ...nudged, w: src.w, h: src.h },
              project?.format_preset ?? null,
              current.kind,
              textOutsetPct(src, project?.format_preset ?? null, current.kind, SPREAD_TEXT_PANEL_PAD),
            )
          : nudged;
      addEl({
        ...rest,
        ...box,
      } as Omit<SpreadElement, 'id' | 'z'>);
    },
    [current, addEl, project?.format_preset],
  );

  const reorderEl = useCallback(
    (elId: string, dir: 1 | -1) => {
      if (!currentId) return;
      mutateElements(currentId, (els) => {
        const sorted = [...els].sort((a, b) => a.z - b.z);
        const i = sorted.findIndex((e) => e.id === elId);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= sorted.length) return els;
        const zi = sorted[i].z;
        const zj = sorted[j].z;
        return els.map((e) =>
          e.id === sorted[i].id ? { ...e, z: zj } : e.id === sorted[j].id ? { ...e, z: zi } : e,
        );
      });
    },
    [currentId, mutateElements],
  );

  const addText = useCallback(() => {
    // New text spawns inside the print-safe area — on interior spreads the
    // default centered box would straddle the gutter fold otherwise.
    const box = clampTextBoxToSafeArea(
      { x: 34, y: 40, w: 32, h: 18 },
      project?.format_preset ?? null,
      current?.kind ?? 'spread',
    );
    addEl({
      type: 'text',
      ...box,
      text: 'Type your words here…',
      // The book-level size chosen in Define → Typography; A− / A+ on the
      // block remain the only way to deviate from it.
      size: textSizeForFontScale(project?.font_scale),
      align: 'left',
      color: 'ink',
    } as Omit<SpreadElement, 'id' | 'z'>);
  }, [addEl, project?.format_preset, project?.font_scale, current?.kind]);

  const canvasAspect = getCanvasAspect(
    project?.format_preset ?? null,
    current?.kind ?? 'spread',
  );

  /** Place a bank tile at canvas coordinates (% of the spread). */
  const addImageAt = useCallback(
    (tile: BankTile, xPct: number, yPct: number) => {
      if (!tile.r2_key) return;
      // Takes generated for a footprint preset land at that footprint, so
      // "half page" arrives half-page-sized (and print-sharp by
      // construction). Preset-less tiles keep the legacy default box.
      const def = IMAGE_SIZE_PRESETS.find(
        (p) => p.value === normalizeSizePreset(tile.bankImage?.size_preset),
      );
      let w: number;
      let h: number;
      if (def) {
        const pages = current?.kind === 'spread' ? 2 : 1;
        if (def.edgeToEdge) {
          w = clamp((Math.min(def.pageW, pages) / pages) * 100, 6, 100);
          h = clamp(def.pageH * 100, 5, 100);
        } else {
          // Floating spot art is square on the page's short side — the
          // same rule the resolver sized the pixels for.
          const { w: pageWIn, h: pageHIn } = formatPageInches(project?.format_preset);
          const sideIn = def.pageW * Math.min(pageWIn, pageHIn);
          w = clamp((sideIn / (pageWIn * pages)) * 100, 6, 100);
          h = clamp((sideIn / pageHIn) * 100, 5, 100);
        }
      } else {
        w = current?.kind === 'spread' ? 34 : 52;
        // Generated takes are square — h% = w% * aspect keeps them square on
        // screen and in the PDF.
        h = clamp(w * canvasAspect * 0.75, 12, 92);
      }
      addEl({
        type: 'image',
        x: clamp(xPct - w / 2, -8, 100 - w / 2),
        y: clamp(yPct - h / 2, -8, 100 - h / 2),
        w,
        h,
        r2_key: tile.r2_key,
        bank_image_id: tile.bankImage?.id ?? null,
        caption: tile.caption,
      } as Omit<SpreadElement, 'id' | 'z'>);
    },
    [addEl, canvasAspect, current?.kind, project?.format_preset],
  );

  // ── Spread operations ─────────────────────────────────────────────────
  const selectSpread = useCallback(
    (id: string) => {
      setCurrentId(id);
      setSelectedEl(null);
    },
    [],
  );

  const step = useCallback(
    (d: 1 | -1) => {
      if (spreads.length === 0) return;
      const n = clamp(idx + d, 0, spreads.length - 1);
      selectSpread(spreads[n].id);
    },
    [spreads, idx, selectSpread],
  );

  const addSpread = useCallback(async (): Promise<boolean> => {
    if (!projectId) return false;
    await flushDirty();
    try {
      const afterId = current?.kind === 'spread' ? current.id : undefined;
      const resp = await api.post<{ spread: Spread }>(`/projects/${projectId}/spreads`, {
        after_spread_id: afterId,
      });
      await refetch();
      if (resp.spread) selectSpread(resp.spread.id);
      return true;
    } catch (err) {
      toastError(showToast, err, 'Failed to add spread');
      return false;
    }
  }, [projectId, current, flushDirty, refetch, selectSpread, showToast]);

  const deleteSpread = useCallback(
    async (spreadId: string): Promise<boolean> => {
      if (!projectId) return false;
      try {
        await api.delete(`/projects/${projectId}/spreads/${spreadId}`);
        dirtyRef.current.delete(spreadId);
        const neighbor =
          spreads[clamp(spreads.findIndex((s) => s.id === spreadId) - 1, 0, spreads.length - 1)];
        await refetch();
        if (neighbor && neighbor.id !== spreadId) selectSpread(neighbor.id);
        showToast('Spread deleted', 'success');
        return true;
      } catch (err) {
        toastError(showToast, err, 'Failed to delete spread');
        return false;
      }
    },
    [projectId, spreads, refetch, selectSpread, showToast],
  );

  const moveSpread = useCallback(
    async (spreadId: string, dir: 1 | -1): Promise<boolean> => {
      if (!projectId) return false;
      const interiors = spreads.filter((s) => s.kind === 'spread').map((s) => s.id);
      const i = interiors.indexOf(spreadId);
      const j = i + dir;
      if (i < 0 || j < 0 || j >= interiors.length) return false;
      [interiors[i], interiors[j]] = [interiors[j], interiors[i]];
      // Optimistic local reorder for instant pager feedback.
      setSpreads((prev) => {
        const cover = prev.filter((s) => s.kind === 'cover');
        const back = prev.filter((s) => s.kind === 'back');
        const byId = new Map(prev.map((s) => [s.id, s]));
        return [
          ...cover,
          ...interiors.map((id) => byId.get(id)!).filter(Boolean),
          ...back,
        ];
      });
      try {
        await api.post(`/projects/${projectId}/spreads/reorder`, { spread_ids: interiors });
        await refetch();
        return true;
      } catch (err) {
        toastError(showToast, err, 'Failed to reorder spreads');
        await refetch();
        return false;
      }
    },
    [projectId, spreads, refetch, showToast],
  );

  // ── Split story (detect beats + compose from manuscript) ─────────────
  const [splitting, setSplitting] = useState(false);

  const splitStory = useCallback(async (): Promise<boolean> => {
    if (!projectId) return false;
    setSplitting(true);
    try {
      await flushDirty();
      await api.post<SplitStoryResponse>(`/projects/${projectId}/ai/split-story`, {});
      dirtyRef.current.clear();
      await refetch();
      showToast('Story beats detected — tune page turns on the story rail', 'success');
      return true;
    } catch (err) {
      toastError(showToast, err, 'Failed to split story');
      return false;
    } finally {
      setSplitting(false);
    }
  }, [projectId, flushDirty, refetch, showToast]);

  // ── Story rail (beats → pages) ────────────────────────────────────────
  const beats: StoryBeat[] = useMemo(
    () => project?.story_beats ?? [],
    [project?.story_beats],
  );
  const beatBreaks = useMemo(
    () => parseBeatBreaks(project?.beat_breaks, beats.length),
    [project?.beat_breaks, beats.length],
  );
  const [regrouping, setRegrouping] = useState(false);

  const updateBeatBreaks = useCallback(
    async (breaks: number[]): Promise<boolean> => {
      if (!projectId || regrouping) return false;
      setRegrouping(true);
      try {
        // The regroup rebuilds interior spreads server-side — push pending
        // canvas edits first, then drop the local dirty state wholesale.
        await flushDirty();
        await api.post<UpdateBeatBreaksResponse>(
          `/projects/${projectId}/beat-breaks`,
          { breaks },
        );
        dirtyRef.current.clear();
        await refetch();
        return true;
      } catch (err) {
        toastError(showToast, err, 'Failed to regroup pages');
        return false;
      } finally {
        setRegrouping(false);
      }
    },
    [projectId, regrouping, flushDirty, refetch, showToast],
  );

  const unreviewedElements = useMemo(
    () => (project?.story_elements ?? []).filter((e) => !e.reviewed_at),
    [project?.story_elements],
  );

  // ── Moments & generation ──────────────────────────────────────────────
  const moments: Moment[] = project?.moments ?? [];
  const [pendingGenerations, setPendingGenerations] = useState<PendingGeneration[]>([]);

  const generateFromMoment = useCallback(
    async (
      momentId: string,
      opts: { count: number; consistencyElementIds: string[]; sizePreset?: ImageSizePreset },
    ): Promise<boolean> => {
      if (!projectId) return false;
      const key = `gen-${momentId}-${Date.now()}`;
      setPendingGenerations((p) => [...p, { key, count: opts.count }]);
      try {
        await api.post<GenerateFromMomentResponse>(
          `/projects/${projectId}/moments/${momentId}/generate`,
          {
            count: opts.count,
            consistency_element_ids: opts.consistencyElementIds,
            ...(opts.sizePreset ? { size_preset: opts.sizePreset } : {}),
          },
        );
        await refetch();
        return true;
      } catch (err) {
        toastError(showToast, err, 'Image generation failed');
        return false;
      } finally {
        setPendingGenerations((p) => p.filter((x) => x.key !== key));
      }
    },
    [projectId, refetch, showToast],
  );

  const generateNew = useCallback(
    async (opts: {
      description: string;
      consistencyElementIds: string[];
      count: number;
      sizePreset?: ImageSizePreset;
    }): Promise<boolean> => {
      if (!projectId) return false;
      const elements = project?.story_elements ?? [];
      const byId = new Map(elements.map((e) => [e.id, e]));
      const picked = opts.consistencyElementIds
        .map((id) => byId.get(id))
        .filter((e): e is StoryElement => !!e);
      try {
        const resp = await api.post<{ moment: Moment }>(`/projects/${projectId}/moments`, {
          description: opts.description,
          character_ids: picked.filter((e) => e.kind === 'character').map((e) => e.id),
          place_id: picked.find((e) => e.kind === 'place')?.id ?? null,
          object_ids: picked.filter((e) => e.kind === 'object').map((e) => e.id),
        });
        return await generateFromMoment(resp.moment.id, {
          count: opts.count,
          consistencyElementIds: opts.consistencyElementIds,
          sizePreset: opts.sizePreset,
        });
      } catch (err) {
        toastError(showToast, err, 'Failed to create the moment');
        return false;
      }
    },
    [projectId, project?.story_elements, generateFromMoment, showToast],
  );

  // ── Bank ──────────────────────────────────────────────────────────────
  const bankTakes: BankTile[] = useMemo(() => {
    const takes = [...(project?.bank_images ?? [])].reverse();
    return takes.map((b) => ({
      key: `take-${b.id}`,
      kind: 'take' as const,
      r2_key: b.r2_key,
      caption: b.caption,
      bankImage: b,
      stale: b.stale,
      stale_reason: b.stale_reason,
    }));
  }, [project?.bank_images]);

  const castTiles: BankTile[] = useMemo(() => {
    const elements = project?.story_elements ?? [];
    const refs = project?.reference_images ?? [];
    return elements.map((el) => {
      // Newest ref = the element's current portrait, matching Define's
      // cast cards and the generation attach order.
      const ownRefs = refs.filter(
        (r) => r.owner_type === 'story_element' && r.owner_id === el.id,
      );
      const ref = ownRefs[ownRefs.length - 1];
      return {
        key: `cast-${el.id}`,
        kind: 'cast' as const,
        r2_key: ref?.r2_key ?? null,
        caption: el.name,
        element: el,
        castKind: el.kind,
      };
    });
  }, [project?.story_elements, project?.reference_images]);

  const deleteBankImage = useCallback(
    async (bankImageId: string): Promise<boolean> => {
      if (!projectId) return false;
      try {
        await api.delete(`/projects/${projectId}/bank-images/${bankImageId}`);
        await refetch();
        return true;
      } catch (err) {
        toastError(showToast, err, 'Failed to remove the image');
        return false;
      }
    },
    [projectId, refetch, showToast],
  );

  const uploadBankImage = useCallback(
    async (file: File): Promise<boolean> => {
      if (!projectId) return false;
      try {
        await api.upload(`/projects/${projectId}/bank-images/upload`, file);
        await refetch();
        return true;
      } catch (err) {
        toastError(showToast, err, 'Failed to upload the image');
        return false;
      }
    },
    [projectId, refetch, showToast],
  );

  // ── Subject cutout (background removal) ──────────────────────────────
  // Runs entirely in the browser (@imgly/background-removal, loaded on
  // first use); the transparent-PNG result uploads as a NEW bank take, so
  // the original image is never modified. One at a time — the model is
  // memory-hungry.
  const [cutoutBankImageId, setCutoutBankImageId] = useState<string | null>(null);

  const cutoutBankImage = useCallback(
    async (bankImageId: string, r2Key: string, caption: string): Promise<boolean> => {
      if (!projectId || cutoutBankImageId) return false;
      setCutoutBankImageId(bankImageId);
      showToast('Cutting out the subject — the first run downloads the model, hang tight…');
      try {
        const res = await fetch(r2Url(r2Key), { credentials: 'include' });
        if (!res.ok) throw new Error(`image fetch failed (${res.status})`);
        const blob = await res.blob();
        const { removeBackground } = await import('@imgly/background-removal');
        const cut = await removeBackground(blob);
        const base = caption.replace(/\.[a-z0-9]+$/i, '').trim().slice(0, 30) || 'image';
        const file = new File([cut], `${base} cutout.png`, { type: 'image/png' });
        await api.upload(`/projects/${projectId}/bank-images/upload`, file);
        await refetch();
        showToast('Subject cut out — added to the bank as a new take', 'success');
        return true;
      } catch (err) {
        toastError(showToast, err, 'Failed to cut out the subject');
        return false;
      } finally {
        setCutoutBankImageId(null);
      }
    },
    [projectId, cutoutBankImageId, refetch, showToast],
  );

  // ── Text refine (per text block) ──────────────────────────────────────
  const [refiningElId, setRefiningElId] = useState<string | null>(null);

  const refineText = useCallback(
    async (elId: string, instruction: string): Promise<boolean> => {
      if (!projectId || !current) return false;
      const el = current.elements.find((e) => e.id === elId);
      if (!el || el.type !== 'text' || !el.text.trim()) return false;
      setRefiningElId(elId);
      try {
        const resp = await api.post<RefineTextResponse>(
          `/projects/${projectId}/ai/refine-text`,
          { text: el.text, instruction },
        );
        updateEl(elId, { text: resp.refined } as Partial<SpreadElement>);
        showToast('Text refined', 'success');
        return true;
      } catch (err) {
        toastError(showToast, err, 'Failed to refine text');
        return false;
      } finally {
        setRefiningElId(null);
      }
    },
    [projectId, current, updateEl, showToast],
  );

  // ── AI-assisted spread layout ─────────────────────────────────────────
  const [layouting, setLayouting] = useState(false);
  /** Snapshot of the elements before the last AI layout — one-shot undo. */
  const [layoutUndo, setLayoutUndo] = useState<{
    spreadId: string;
    elements: SpreadElement[];
  } | null>(null);

  const layoutSpread = useCallback(async (): Promise<boolean> => {
    if (!projectId || !current || current.elements.length === 0) return false;
    setLayouting(true);
    try {
      // The endpoint reads the spread from the DB — push pending canvas
      // edits first so the AI lays out what the author actually sees.
      await flushDirty();
      const before = current.elements;
      const resp = await api.post<LayoutSpreadResponse>(
        `/projects/${projectId}/ai/layout-spread`,
        { spread_id: current.id },
      );
      setLayoutUndo({ spreadId: current.id, elements: before });
      mutateElements(current.id, () => resp.elements);
      showToast('Spread laid out — press Undo layout to revert', 'success');
      return true;
    } catch (err) {
      toastError(showToast, err, 'Failed to lay out the spread');
      return false;
    } finally {
      setLayouting(false);
    }
  }, [projectId, current, flushDirty, mutateElements, showToast]);

  const undoLayout = useCallback(() => {
    if (!layoutUndo) return;
    mutateElements(layoutUndo.spreadId, () => layoutUndo.elements);
    setLayoutUndo(null);
  }, [layoutUndo, mutateElements]);

  // ── Stage navigation ──────────────────────────────────────────────────
  const goToStage = useCallback(
    async (stage: WizardStage) => {
      // Element edits PATCH their spread but never refresh useProject's
      // copy — flush pending saves and re-read before switching so the
      // next stage (Export builds the PDF from project.spreads) sees the
      // canvas exactly as composed.
      await flushDirty();
      await refetch();
      await updateProject({ stage });
    },
    [flushDirty, refetch, updateProject],
  );

  return {
    project,
    refetch,
    spreads,
    current,
    currentId,
    idx,
    canvasAspect,
    formatPreset: project?.format_preset ?? null,
    labelFor,
    interiorIndexOf,
    selectedEl,
    setSelectedEl,
    saveState,
    // elements
    updateEl,
    addEl,
    removeEl,
    duplicateEl,
    reorderEl,
    addText,
    addImageAt,
    // spreads
    selectSpread,
    step,
    addSpread,
    deleteSpread,
    moveSpread,
    // split + story rail
    splitting,
    splitStory,
    unreviewedElements,
    beats,
    beatBreaks,
    regrouping,
    updateBeatBreaks,
    // generation + bank
    moments,
    pendingGenerations,
    generateFromMoment,
    generateNew,
    bankTakes,
    castTiles,
    deleteBankImage,
    uploadBankImage,
    cutoutBankImageId,
    cutoutBankImage,
    // text refine
    refiningElId,
    refineText,
    // AI layout
    layouting,
    layoutSpread,
    layoutUndo,
    undoLayout,
    // navigation
    goToStage,
    updateStoryElement: projectHook.updateStoryElement,
  };
}

export type ComposeStore = ReturnType<typeof useComposeStage>;
